import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "../lib/api";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "ai", text: "Namaste! I'm Aasha, your AHCCT assistant. Ask me anything about our programs, donations, or how you can help." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => "sess_" + Math.random().toString(36).slice(2, 10));
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "ai", text: data.response }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I'm having trouble right now. Please try again or reach us at +91 8002485087." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Open chatbot"
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-50 bg-[#000080] hover:bg-[#000066] text-white rounded-full p-4 shadow-2xl transition-transform hover:scale-110"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-[#eae5d9] flex flex-col overflow-hidden fade-in" data-testid="chatbot-panel">
          <div className="bg-[#000080] text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF9933] flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold">Aasha • AHCCT Assistant</div>
              <div className="text-xs text-white/70">Powered by AI • Always here to help</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FDFBF7]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#FF9933] text-white rounded-br-sm" : "bg-white border border-[#eae5d9] text-[#1A1A1A] rounded-bl-sm"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#eae5d9] px-4 py-2.5 rounded-2xl text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-[#eae5d9] p-3 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about donations, programs, CSR..."
              data-testid="chatbot-input"
              className="flex-1 px-4 py-2.5 rounded-full border border-[#eae5d9] text-sm focus:outline-none focus:border-[#FF9933]"
            />
            <button onClick={send} disabled={loading} data-testid="chatbot-send" className="bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full p-2.5 disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
